import { useRef, useState } from "react";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { Alert, Box, Button, CircularProgress, Stack } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { deleteMyPhoto, uploadMyPhoto } from "@/api/profile";

import { PractitionerAvatar } from "./PractitionerAvatar";

type Props = {
  practitionerId: string;
  hasPhoto: boolean;
  displayName: string;
  onChanged: () => void;
};

export function PhotoUploader({ practitionerId, hasPhoto, displayName, onChanged }: Props) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bust, setBust] = useState(0);

  const upload = useMutation({
    mutationFn: (file: File) => uploadMyPhoto(file),
    onSuccess: () => {
      setError(null);
      setBust(Date.now());
      onChanged();
    },
    onError: (e: unknown) => {
      const d = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(d ?? (e as Error).message);
    },
  });

  const del = useMutation({
    mutationFn: () => deleteMyPhoto(),
    onSuccess: () => {
      setBust(Date.now());
      onChanged();
    },
  });

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center">
        <PractitionerAvatar
          practitionerId={practitionerId}
          hasPhoto={hasPhoto}
          displayName={displayName}
          cacheBust={bust}
          sx={{ width: 72, height: 72, fontSize: 24 }}
        />
        <Stack spacing={1}>
          <input
            ref={inputRef}
            type="file"
            hidden
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f);
            }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={upload.isPending ? <CircularProgress size={14} /> : <CloudUploadIcon />}
              variant="outlined"
              onClick={() => inputRef.current?.click()}
              disabled={upload.isPending}
            >
              {t("profile.uploadPhoto")}
            </Button>
            {hasPhoto && (
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => del.mutate()}
                disabled={del.isPending}
              >
                {t("profile.removePhoto")}
              </Button>
            )}
          </Stack>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </Stack>
    </Box>
  );
}
